import { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { setUnauthorizedHandler } from "./api/client";
import { fetchSession, logout, type Session } from "./api/auth";
import { PermissionProvider, buildPermissions } from "./api/permissions";
import { AgentDetail } from "./pages/AgentDetail";
import { AgentPanel } from "./pages/AgentPanel";
import { ApplicationDetail } from "./pages/ApplicationDetail";
import { ApplicationList } from "./pages/ApplicationList";
import { Dashboard } from "./pages/Dashboard";
import { DocumentDetail } from "./pages/DocumentDetail";
import { DocumentList } from "./pages/DocumentList";
import { Login } from "./pages/Login";
import { Platform } from "./pages/Platform";
import { Quality } from "./pages/Quality";
import { PlatformService } from "./pages/PlatformService";
import { SubmitConsole } from "./pages/SubmitConsole";
import { Roles } from "./pages/Roles";
import { Usage } from "./pages/Usage";
import { Users } from "./pages/Users";
import { Loading, ErrorState } from "./components/States";

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    fetchSession()
      .then((s) => {
        setSession(s);
        setError(null);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  }, []);

  useEffect(refresh, [refresh]);

  // A session can lapse mid-browse. Rather than every panel showing its own
  // error, the first 401 re-checks the session, which drops the reader back to
  // the sign-in screen.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSession((prev) =>
        prev?.authenticated ? { ...prev, authenticated: false } : prev,
      );
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  if (error) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <ErrorState message={error} onRetry={refresh} />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <Loading what="session" />
        </div>
      </div>
    );
  }

  if (!session.authenticated) {
    return <Login session={session} onSignedIn={refresh} />;
  }

  // Built from the server's answer rather than from a role name, so the nav
  // and the API cannot disagree about who may see what.
  const perms = buildPermissions(session);

  //: Nav in the order a reader works: what ran, what it produced, what it cost.
  //: Each entry names the permission that gates it; the server supplies the
  //: same map, so a page gated there disappears from here without a UI change.
  const NAV: { to: string; label: string }[] = [
    { to: "/agents", label: "Agents" },
    // Next to Agents: it answers "how are they doing" about the same
    // registry, and shares agents:view so it appears for the same people.
    { to: "/quality", label: "Quality" },
    { to: "/documents", label: "Documents" },
    { to: "/applications", label: "Applications" },
    { to: "/dashboard", label: "Dashboard" },
    // Who submitted what — after Dashboard, which answers the same question
    // for the platform as a whole.
    { to: "/usage", label: "Usage" },
    { to: "/platform", label: "Platform" },
    { to: "/submit", label: "Submit" },
    { to: "/users", label: "Users" },
    { to: "/roles", label: "Roles" },
  ];

  const visible = NAV.filter((n) => perms.canSee(n.to));
  // Where "/" should land: the first page this person can actually open.
  // Sending everyone to /agents meant an operator who cannot see agents
  // arrived on a 403 as their first impression of the console.
  const home = visible[0]?.to ?? "/no-access";

  return (
    <PermissionProvider value={perms}>
    <div className="shell">
      <header className="topbar">
        <span className="brand">PPD Agent Platform</span>
        {/* Presentation only. Every route behind these links re-checks the
            same permission server-side, because a missing link stops nobody
            from typing a URL. What hiding buys is that people are not offered
            controls that will refuse them. */}
        <nav className="nav">
          {visible.map((n) => (
            <NavLink key={n.to} to={n.to}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="topbar-right">
          {session.mode === "disabled" ? (
            <span
              className="pill pill-critical"
              title="ORCHESTRATOR_AUTH_MODE=disabled — every /api/orchestrator route is public, including the original document scans."
            >
              auth disabled
            </span>
          ) : null}
          <span className="muted">{session.subject}</span>
          <button
            onClick={() => {
              logout().finally(refresh);
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="main">
        {/* A route is registered only when its permission is held, so someone
            who types a URL they cannot use gets the ordinary "page not found"
            rather than a screen that renders and then fills with 403s. The
            detail routes follow their list: a person who cannot see the
            document table has no business deep-linking into one. */}
        <Routes>
          <Route path="/" element={<Navigate to={home} replace />} />
          {perms.canSee("/agents") ? (
            <>
              <Route path="/agents" element={<AgentPanel />} />
              <Route path="/agents/:type" element={<AgentDetail />} />
              <Route path="/quality" element={<Quality />} />
            </>
          ) : null}
          {perms.canSee("/documents") ? (
            <>
              <Route path="/documents" element={<DocumentList />} />
              <Route path="/documents/:id" element={<DocumentDetail />} />
            </>
          ) : null}
          {perms.canSee("/applications") ? (
            <>
              <Route path="/applications" element={<ApplicationList />} />
              <Route path="/applications/:id" element={<ApplicationDetail />} />
            </>
          ) : null}
          {perms.canSee("/dashboard") ? (
            <Route path="/dashboard" element={<Dashboard />} />
          ) : null}
          {perms.canSee("/usage") ? <Route path="/usage" element={<Usage />} /> : null}
          {perms.canSee("/platform") ? (
            <>
              <Route path="/platform" element={<Platform />} />
              <Route path="/platform/:key" element={<PlatformService />} />
            </>
          ) : null}
          {perms.canSee("/submit") ? (
            <Route path="/submit" element={<SubmitConsole />} />
          ) : null}
          {perms.canSee("/users") ? <Route path="/users" element={<Users />} /> : null}
          {perms.canSee("/roles") ? <Route path="/roles" element={<Roles />} /> : null}
          {/* An account with no page at all. Rare, and the result of a role
              stripped of every view permission — which is worth saying plainly
              rather than rendering an empty shell. */}
          <Route
            path="/no-access"
            element={
              <div className="card">
                <div className="card-body">
                  <h1 className="page-title">No pages are available</h1>
                  <p className="page-sub">
                    Your role grants no view permissions, so there is nothing to
                    show. Ask an administrator to grant your role access to at
                    least one page.
                  </p>
                </div>
              </div>
            }
          />
          <Route
            path="*"
            element={
              <div className="card">
                <div className="state">
                  <strong>Page not found</strong>
                </div>
              </div>
            }
          />
        </Routes>
      </main>
    </div>
    </PermissionProvider>
  );
}
